import React, { useState, useEffect, useRef, useCallback } from 'react';
import { useNavigate, useLocation, useSearchParams } from 'react-router-dom';
import { Search, X } from 'lucide-react';

const SEARCHABLE_ROUTES = [
  /^\/feed/,
  /^\/panels\/[^/]+$/,
  /^\/cases\/mine/,
  /^\/cases\/saved/,
  /^\/cases\/my-inputs/,
  /^\/challenges$/,
];

const SearchBar = () => {
  const location = useLocation();
  const [searchParams, setSearchParams] = useSearchParams();
  const navigate = useNavigate();

  const currentUrlQuery = searchParams.get('q') || '';
  const [query, setQuery] = useState(currentUrlQuery);
  const inputRef = useRef(null);
  const debounceTimerRef = useRef(null);

  const isSearchablePage = SEARCHABLE_ROUTES.some((pattern) => pattern.test(location.pathname));

  // Sync input value if URL q parameter changes externally
  useEffect(() => {
    setQuery(currentUrlQuery);
  }, [currentUrlQuery]);

  // Debounced URL search parameter update for live in-page filtering
  const updateUrlSearch = useCallback((newQuery) => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    debounceTimerRef.current = setTimeout(() => {
      if (isSearchablePage) {
        const newParams = new URLSearchParams(searchParams);
        const trimmed = newQuery.trim();
        if (trimmed) {
          newParams.set('q', trimmed);
        } else {
          newParams.delete('q');
        }
        setSearchParams(newParams, { replace: true });
      }
    }, 250);
  }, [isSearchablePage, searchParams, setSearchParams]);

  const handleChange = (e) => {
    const val = e.target.value;
    setQuery(val);
    updateUrlSearch(val);
  };

  const handleSearchSubmit = (e) => {
    e.preventDefault();
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }

    const trimmed = query.trim();
    if (!trimmed) {
      if (isSearchablePage && searchParams.has('q')) {
        const newParams = new URLSearchParams(searchParams);
        newParams.delete('q');
        setSearchParams(newParams);
      }
      return;
    }

    if (isSearchablePage) {
      const newParams = new URLSearchParams(searchParams);
      newParams.set('q', trimmed);
      setSearchParams(newParams);
    } else {
      // If on non-searchable route (e.g. Dashboard), navigate to Feed with q parameter
      navigate(`/feed?q=${encodeURIComponent(trimmed)}`);
    }
  };

  const handleClear = () => {
    if (debounceTimerRef.current) {
      clearTimeout(debounceTimerRef.current);
    }
    setQuery('');
    if (isSearchablePage && searchParams.has('q')) {
      const newParams = new URLSearchParams(searchParams);
      newParams.delete('q');
      setSearchParams(newParams);
    }
    inputRef.current?.focus();
  };

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (debounceTimerRef.current) {
        clearTimeout(debounceTimerRef.current);
      }
    };
  }, []);

  return (
    <form onSubmit={handleSearchSubmit} className="relative flex items-center flex-1 max-w-[520px]">
      <div className="relative w-full">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400 pointer-events-none" size={16} />
        <input
          id="global-search-bar"
          ref={inputRef}
          type="text"
          value={query}
          onChange={handleChange}
          placeholder="Search cases, specialties, or keywords..."
          autoComplete="off"
          className="w-full pl-9 pr-9 py-2 text-sm border border-gray-200 rounded-lg bg-gray-50
                     focus:outline-none focus:border-indigo-400 focus:ring-2 focus:ring-indigo-100
                     placeholder:text-gray-400 transition-all duration-200"
        />
        {query && (
          <button
            type="button"
            onClick={handleClear}
            className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 transition-colors"
            aria-label="Clear search"
          >
            <X size={15} />
          </button>
        )}
      </div>
    </form>
  );
};

export default SearchBar;